import View from './view.js'
import { normalizePath } from 'Simple/Flow.js'
import { DataProvider } from 'Simple/Data.js'
import makeDebug from 'Simple/debug'
import pluralize from 'pluralize'
import React from 'react'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'

let debug = makeDebug('simple/morph/view')

function SortableItem(props) {
  let { item, list, index, dataContextName, viewPath, childName, id } = props

  return (
    <Draggable key={id} draggableId={id} index={index}>
      {(provided, snapshot) => (
        <div ref={provided.innerRef} {...provided.draggableProps}>
          <ListItem
            {...props}
            key={item?.id || index}
            context={dataContextName}
            item={list[index]}
            index={index}
            list={list}
            hasItemKey={false}
            viewPath={`${viewPath}/${childName}(${list[index]?.id || index})`}
            parentViewPath={viewPath}
          >
            {typeof props.children === 'function'
              ? props.children({
                  index: item?.id || index,
                  item: list[index],
                  indexReverse: list.length - item.index,
                  isFirst: item.index === 0,
                  isLast: item.index === list.length - 1,
                  viewPath: `${viewPath}/${childName}(${
                    list[index]?.id || index
                  })`,
                  dsViewPath: `${viewPath}/${childName}(${
                    list[index]?.id || index
                  })`,
                  dragHandleProps: provided.dragHandleProps,
                })
              : props.children}
          </ListItem>
        </div>
      )}
    </Draggable>
  )
}

function ListItem(props) {
  let valueItem = React.useMemo(
    () => ({
      index: props.index,
      indexReverse: props.list.length - props.index,
      isFirst: props.index === 0,
      isLast: props.index === props.list.length - 1,
    }),
    [props.index, props.list]
  )

  let maybeListActions = {
    ...(typeof props.useListItemDataOnSubmit === 'function' && {
      onSubmit: props.useListItemDataOnSubmit(props),
    }),
    ...(typeof props.useListItemDataOnChange === 'function' && {
      onChange: props.useListItemDataOnChange({
        ...props,
        viewPath: props.parentViewPath,
      }),
    }),
  }

  if (
    process.env.NODE_ENV === 'development' &&
    !props.hasItemKey &&
    !props.item?.id
  ) {
    debug({
      type: 'simple/data',
      warning: `Missing "id" property on item in context "${props.context}". The list's index will be used as a fallback key. Consider setting "itemKey" if the item has a custom or compound key e.g. first_name,last_name.`,
      item: props.item,
      context: props.context,
      viewPath: props.viewPath,
    })
  }
  return (
    <DataProvider
      context={props.context}
      value={props.item}
      viewPath={props.viewPath}
      {...maybeListActions}
    >
      <DataProvider
        context={`${props.context}_item`}
        value={valueItem}
        viewPath={props.viewPath}
      >
        {props.children}
      </DataProvider>
    </DataProvider>
  )
}

function defaultItemDataContextName(data) {
  if (!data) return ''
  return pluralize.singular(data)
}

export default function Logic(props) {
  let list = props?.from || []
  list = [...list].sort((a, b) => a?.order - b?.order)

  let dataContextName =
    props.itemDataContextName || defaultItemDataContextName(props?.data)
  let childName = props?.childName || dataContextName
  let viewPath = normalizePath(props.viewPath, '..').slice(0, -1)

  return (
    <View viewPath={props.viewPath}>
      <DragDropContext onDragEnd={props.onDragEnd}>
        <Droppable droppableId="list">
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={`space-y-2 p-4 rounded-lg transition-colors ${
                snapshot.isDraggingOver ? 'bg-blue-50' : 'bg-white'
              }`}
            >
              {list.map((item, index) => (
                <SortableItem
                  {...props}
                  id={item?.id || index + 1}
                  index={index}
                  item={item}
                  list={list}
                  childName={childName}
                  dataContextName={dataContextName}
                  viewPath={viewPath}
                ></SortableItem>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </View>
  )
}
